import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Text,
  Flex,
  Input,
  Textarea,
  Button,
  Tag,
  useToast,
  Spinner,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  InputGroup,
  InputLeftElement,
  useDisclosure,
  Tooltip,
  Wrap,
  WrapItem,
  Icon,
} from '@chakra-ui/react';
import { DeleteIcon, CloseIcon, SearchIcon, ChevronRightIcon, NotAllowedIcon, WarningIcon } from '@chakra-ui/icons';
import { LuSplit, LuRotateCcw } from 'react-icons/lu';
import { Trans, useTranslation } from 'react-i18next';
import { useDisplayAmount } from '../../contexts/ConvertedAmountsContext';
import i18n from '../../i18n';
import TagPickerPopover from '../shared/TagPickerPopover';
import CalendarPicker from '../shared/CalendarPicker';
import { usePreferences } from '../../contexts/PreferencesContext';
import useGoalsEnabled from '../../hooks/useGoalsEnabled';
import { useNarrowerThan } from '../../hooks/useNarrowerThan';
import useSimilarMovements from '../../hooks/useSimilarMovements';
import {
  fetchMovementById,
  updateMovement,
  deleteMovement,
  ignoreMovement,
  restoreMovement,
  postMovement,
  parseMovement,
  getReferenceData,
  fetchGoals,
} from '../../api/endpointsBackend';
import { formatMoney } from '../../utils/formatters';
import { DEFAULT_CATEGORY_COLOR } from '../categories/CategoriesPage';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import { getMissingFieldsMessage } from '../../utils/movementTransformers';
import { todayLocalISO } from '../../utils/localDate';
import { isAiBudgetRefusal } from '../../utils/aiUsage';
import { similarLine } from '../../utils/similarMovements';
import { amountToText } from '../../utils/amountParser';
import useMovementDraft, { toAmount, tradeFields } from '../../hooks/useMovementDraft';
import { tradeSide, formatPrice } from '../../utils/subAccounts';
import MovementsList from '../shared/MovementsList';
import RateSourceTooltip from '../shared/RateSourceTooltip';
import { movementAlertReasons } from '../../utils/movementAlertReasons';
import { useReadTwiceStatement } from '../../hooks/useStatementRepair';
import { CHECKUP_PATH } from '../repairs/checkup/CheckupBanner';
import PhoneMovementDetail from '../phone/PhoneMovementDetail';
import IgnoreReasonField from './IgnoreReasonField';


// Format a date string (YYYY-MM-DD) with localized weekday and month names.
/**
 * Below this the label column and its value stop fitting side by side
 * (MI-336). Measured on the panel itself, never on the viewport: the panel is
 * resizable now, so a `md` breakpoint says nothing about how much room these
 * fields actually have.
 */
const COMPACT_WIDTH = 380;

/**
 * A field's label. Narrower when the panel is narrow, so the value beside it
 * keeps as much room as possible — but always on the same line as its value.
 * The value is what truncates under pressure, not the row's shape.
 */
const FieldLabel = ({ compact, children }) => (
  <Text
    fontSize={{ base: "sm", md: "md" }}
    color="redesign.text.muted"
    w={compact ? "68px" : { base: "70px", md: "100px" }}
    flexShrink={0}
    // "Categoría" / "Etiquetas" are the longest labels this column has to
    // hold; isTruncated is the safety net for a translation that turns out
    // longer than 68px allows.
    isTruncated
  >
    {children}
  </Text>
);

function formatDateLong(dateString) {
  const [year, month, day] = dateString.split('-').map(Number);
  const months = i18n.t('months.long', { ns: 'dates', returnObjects: true });
  const daysOfWeek = i18n.t('days.long', { ns: 'dates', returnObjects: true });
  const localDate = new Date(year, month - 1, day);
  const dayOfWeek = daysOfWeek[localDate.getDay()];
  return `${dayOfWeek}, ${months[month - 1]} ${day}, ${year}`;
}

// Movement type options — values are sent to backend (in English); labels are translated at render time
const TYPE_OPTIONS = [
  { value: 'Income', labelKey: 'types.income' },
  { value: 'Expenses', labelKey: 'types.expenses' },
  { value: 'Transfer', labelKey: 'types.transfer' }
];

// Currency picker popover
function CurrencyPicker({ value, currencies, onChange, children, placement = "bottom" }) {
  return (
    <Popover placement={placement}>
      {({ onClose }) => (
        <>
          <PopoverTrigger>{children}</PopoverTrigger>
          <PopoverContent w="120px" boxShadow="lg" borderRadius="lg">
            <PopoverBody p={1}>
              {currencies.map(curr => (
                <Flex
                  key={curr.name}
                  px={3}
                  py={2}
                  cursor="pointer"
                  borderRadius="md"
                  bg={curr.name === value ? "redesign.bg.accent" : "transparent"}
                  _hover={{ bg: curr.name === value ? "redesign.bg.accent" : "redesign.bg.hover" }}
                  onClick={() => { onChange(curr.name); onClose(); }}
                  align="center"
                >
                  <Text fontSize="md" fontWeight={curr.name === value ? "600" : "400"}>
                    {curr.name}
                  </Text>
                </Flex>
              ))}
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}

// Custom dropdown component (single select). Also the account and category
// picker of the account panel's forms.
export function CustomDropdown({ value, options, onChange, showInactiveIndicator = false, defaultColor = null, isClearable = false }) {
  const { t } = useTranslation('movements');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState('');

  // Filter by search and sort: active first, inactive last
  const filteredOptions = options
    .filter(opt => opt.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (showInactiveIndicator) {
        // Active accounts first (is_active: true), inactive last
        if (a.is_active !== b.is_active) {
          return a.is_active ? -1 : 1;
        }
      }
      return 0; // Keep original order within same group
    });

  const selectedOption = options.find(opt => opt.id === value);
  const isSelectedInactive = showInactiveIndicator && selectedOption && selectedOption.is_active === false;
  const tagColor = selectedOption?.color || defaultColor;
  const tagColorProps = tagColor
    ? { bg: `${tagColor}20`, color: tagColor }
    : { colorScheme: "gray" };

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start">
      <PopoverTrigger>
        <Flex
          align="center"
          gap={2}
          cursor="pointer"
          _hover={{ opacity: 0.8 }}
          // MI-336 — the panel is resizable and can get narrow. Without this
          // chain of minW={0}, a flex item's default min-width is its content
          // size, which is what let a long account name push the row wider
          // than the panel instead of truncating inside it.
          flex="1"
          minW={0}
        >
          {selectedOption ? (
            <Flex align="center" gap={1} minW={0}>
              <Tag
                size="md"
                {...tagColorProps}
                variant="subtle"
                borderRadius="full"
                px={3}
                py={1}
                opacity={isSelectedInactive ? 0.6 : 1}
                minW={0}
                maxW="100%"
              >
                <Flex align="center" gap="6px" minW={0}>
                  {isSelectedInactive && <NotAllowedIcon color="redesign.text.muted" boxSize={3} flexShrink={0} />}
                  {selectedOption.icon && <Text flexShrink={0}>{selectedOption.icon}</Text>}
                  <Text isTruncated minW={0}>{selectedOption.name}</Text>
                </Flex>
              </Tag>
              {isClearable && (
                <CloseIcon
                  boxSize="6px"
                  color="redesign.text.muted"
                  cursor="pointer"
                  flexShrink={0}
                  _hover={{ color: "redesign.text" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                />
              )}
            </Flex>
          ) : (
            <Flex
              align="center"
              justify="center"
              w="28px"
              h="28px"
              borderRadius="full"
              border="1px solid"
              borderColor="redesign.border"
              color="redesign.text.muted"
              _hover={{ borderColor: "redesign.border", color: "redesign.text.muted" }}
              fontSize="md"
              fontWeight="300"
            >+</Flex>
          )}
        </Flex>
      </PopoverTrigger>
      <PopoverContent w="280px" boxShadow="lg" borderRadius="lg">
        <PopoverBody p={0}>
          {/* Search */}
          <Box p={2} borderBottom="1px solid" borderColor="redesign.border">
            <InputGroup size="sm">
              <InputLeftElement>
                <SearchIcon color="redesign.text.muted" />
              </InputLeftElement>
              <Input
                placeholder={t('shared.categoryPicker.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                variant="filled"
                borderRadius="md"
                _focus={{ bg: "redesign.bg.card" }}
              />
            </InputGroup>
          </Box>

          {/* Options */}
          <Box maxH="200px" overflowY="auto" py={1}>
            {filteredOptions.map(opt => {
              const isInactive = showInactiveIndicator && opt.is_active === false;
              return (
                <Flex
                  key={opt.id}
                  px={3}
                  py={2}
                  cursor="pointer"
                  bg={opt.id === value ? "redesign.bg.accent" : "transparent"}
                  _hover={{ bg: opt.id === value ? "redesign.bg.accent" : "redesign.bg.hover" }}
                  onClick={() => {
                    onChange(opt);
                    onClose();
                    setSearch('');
                  }}
                  align="center"
                  gap={2}
                  opacity={isInactive ? 0.6 : 1}
                >
                  {isInactive && <NotAllowedIcon color="redesign.text.muted" boxSize={3} />}
                  {opt.icon && <Text fontSize="md">{opt.icon}</Text>}
                  <Text
                    fontSize="md"
                    fontWeight={opt.id === value ? "600" : "400"}
                    color={isInactive ? "redesign.text.muted" : "inherit"}
                  >
                    {opt.name}
                  </Text>
                </Flex>
              );
            })}
            {filteredOptions.length === 0 && (
              <Text fontSize="md" color="redesign.text.muted" px={3} py={2}>
                {t('detailPanel.noResults')}
              </Text>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

// Custom multi-select dropdown component
function MultiSelectDropdown({ selectedIds = [], options, onAdd, onRemove }) {
  const selectedOptions = options.filter(opt => selectedIds.includes(opt.id));
  const selectedIdSet = new Set(selectedIds);

  const handleToggle = (tag) => {
    if (selectedIdSet.has(tag.id)) {
      onRemove(tag.id);
    } else {
      onAdd(tag);
    }
  };

  const trigger = selectedOptions.length > 0 ? (
    // MI-336 — minW={0} down the chain so the first tag's name is what gives
    // way on a narrow panel, not the row. The "+N" badge is short and static,
    // so it keeps flexShrink={0} — it never needs to truncate.
    <Flex align="center" gap={2} minW={0} flex="1">
      {/* First tag as pill with colored dot + name */}
      <Tag
        size="md"
        variant="subtle"
        borderRadius="full"
        px={3}
        py={1}
        cursor="pointer"
        border="1px solid"
        borderColor="redesign.border"
        bg="redesign.bg.surface"
        _hover={{ borderColor: "redesign.border" }}
        minW={0}
        maxW="100%"
      >
        <Box w="7px" h="7px" borderRadius="full" bg={selectedOptions[0].color || "#3182CE"} mr={2} flexShrink={0} />
        <Text fontSize="sm" isTruncated minW={0} color="redesign.text">{selectedOptions[0].name}</Text>
      </Tag>
      {/* Overflow: colored dots + "+N" count */}
      {selectedOptions.length > 1 && (
        <Tag
          size="md"
          variant="subtle"
          borderRadius="full"
          px={3}
          py={1}
          border="1px solid"
          borderColor="redesign.border"
          bg="redesign.bg.surface"
          flexShrink={0}
        >
          <Flex align="center" gap="3px" mr="4px">
            {selectedOptions.slice(1, 4).map(opt => (
              <Box key={opt.id} w="7px" h="7px" borderRadius="full" bg={opt.color || "#3182CE"} />
            ))}
          </Flex>
          <Text fontSize="sm" color="redesign.text">+{selectedOptions.length - 1}</Text>
        </Tag>
      )}
    </Flex>
  ) : (
    <Flex
      align="center"
      justify="center"
      w="28px"
      h="28px"
      borderRadius="full"
      border="1px solid"
      borderColor="redesign.border"
      color="redesign.text.muted"
      cursor="pointer"
      _hover={{ borderColor: "redesign.border", color: "redesign.text.muted" }}
      fontSize="md"
      fontWeight="300"
    >+</Flex>
  );

  return (
    <TagPickerPopover allTags={options} selectedTagIds={selectedIdSet} onToggle={handleToggle} placement="bottom-start">
      {trigger}
    </TagPickerPopover>
  );
}

// Read-only Popover that displays source filenames as a dropdown trigger
function SourceFilesPopover({ sourceFiles }) {
  const { t } = useTranslation('movements');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState('');
  const [first, ...rest] = sourceFiles;

  const hasOverflow = rest.length > 0;
  const filteredFiles = sourceFiles.filter(f =>
    f.toLowerCase().includes(search.toLowerCase())
  );

  const handleClose = () => {
    onClose();
    setSearch('');
  };

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={handleClose} placement="bottom-start">
      <PopoverTrigger>
        <Flex align="center" gap={1} cursor="pointer" _hover={{ opacity: 0.8 }} flex="1" minW={0}>
          <Tooltip label={first} placement="top" hasArrow>
            <Tag
              size="md"
              variant="subtle"
              borderRadius="full"
              px={3}
              py={1}
              maxW="220px"
              minW="80px"
              border="1px solid"
              borderColor="redesign.border"
              bg="redesign.bg.surface"
            >
              <Text isTruncated fontSize="sm" color="redesign.text">{first}</Text>
              {hasOverflow && (
                <Text fontSize="sm" color="redesign.text.muted" ml={2} flexShrink={0}>+{rest.length}</Text>
              )}
            </Tag>
          </Tooltip>
          <ChevronRightIcon color="redesign.text.muted" flexShrink={0} />
        </Flex>
      </PopoverTrigger>
      <PopoverContent w="280px" boxShadow="lg" borderRadius="lg">
        <PopoverBody p={0}>
          <Box p={2} borderBottom="1px solid" borderColor="redesign.border">
            <InputGroup size="sm">
              <InputLeftElement>
                <SearchIcon color="redesign.text.muted" />
              </InputLeftElement>
              <Input
                placeholder={t('shared.categoryPicker.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                variant="filled"
                borderRadius="md"
                _focus={{ bg: "redesign.bg.card" }}
              />
            </InputGroup>
          </Box>
          <Box maxH="200px" overflowY="auto" py={1}>
            {filteredFiles.map((filename, idx) => (
              <Text
                key={idx}
                px={3}
                py={2}
                fontSize="sm"
                color="redesign.text"
                wordBreak="break-all"
              >
                {filename}
              </Text>
            ))}
            {filteredFiles.length === 0 && (
              <Text fontSize="sm" color="redesign.text.muted" px={3} py={2}>
                {t('detailPanel.noResults')}
              </Text>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

const idFilters = (ids) => (
  ids && ids.length > 0 ? [{ entity: 'Movement', column: 'id', operator: 'in', values: ids }] : []
);

// One group of similar movements, with "N more" when the backend sent only
// the newest of it.
function SimilarGroup({ label, filters, more, categories, allTags, onSelectMovement }) {
  const { t } = useTranslation('movements');
  if (filters.length === 0) return null;
  return (
    <Box mt={4} pt={3} borderTop="1px solid" borderColor="redesign.border">
      <Text fontSize="md" fontWeight={600} color="redesign.heading">
        {label}
      </Text>
      <MovementsList
        filters={filters}
        categories={categories}
        allTags={allTags}
        showAccountName
        onMovementClick={onSelectMovement}
      />
      {more > 0 && (
        <Text fontSize="sm" color="redesign.text.muted">
          {t('detailPanel.similar.more', { count: new Intl.NumberFormat('en-US').format(more) })}
        </Text>
      )}
    </Box>
  );
}

// MI-345: the rest of the movement's group, named by the group's name and
// type. The phone sheet names the group in its own rows.
export function GroupSection({ movement, categories, allTags, onSelectMovement }) {
  const { t } = useTranslation('movements');
  const groupId = movement?.movement_group_id;
  const filters = useMemo(() => (groupId ? [
    { entity: 'Movement', column: 'movement_group_id', operator: 'in', values: [groupId] },
    { entity: 'Movement', column: 'id', operator: 'not_in', values: [movement.id] },
  ] : []), [groupId, movement?.id]);

  if (!groupId) return null;
  const group = movement.movement_group;
  const type = group?.type && t(`modals.group.types.${group.type}`, { defaultValue: group.type });
  return (
    <Box px={{ base: 2, md: 4 }} pt={4}>
      <Text fontSize="sm" fontWeight="bold" color="redesign.heading">
        {t('detailPanel.group.title')}
      </Text>
      <Text fontSize="sm" color="redesign.text.muted" mt={1}>
        {[group?.name, type].filter(Boolean).join(' · ')}
      </Text>
      <MovementsList
        filters={filters}
        categories={categories}
        allTags={allTags}
        showAccountName
        onMovementClick={onSelectMovement}
      />
    </Box>
  );
}

function SimilarMovementsSection({ movementId, categories, allTags, onSelectMovement }) {
  const { t } = useTranslation('movements');
  const { similar, loading } = useSimilarMovements(movementId);

  const sameChargeFilters = useMemo(() => idFilters(similar?.same_charge_ids), [similar]);
  const otherFilters = useMemo(() => idFilters(similar?.other_ids), [similar]);

  // While the lookup is in flight, say so (MI-311). It used to render nothing,
  // which is indistinguishable from "this movement has no similar ones" — and
  // the request is slow enough for that to be the state you usually see first.
  if (loading) {
    return (
      <Box px={{ base: 2, md: 4 }} pt={4} pb={6}>
        <Text fontSize="sm" fontWeight="bold" color="redesign.heading" mb={2}>
          {t('detailPanel.similar.title')}
        </Text>
        <Flex align="center" gap={2} py={2}>
          {/* label overrides Chakra's hardcoded English "Loading..." in the
              screen-reader text. */}
          <Spinner size="xs" color="redesign.text.muted" label={t('detailPanel.similar.loading')} />
          <Text fontSize="sm" color="redesign.text.muted">
            {t('detailPanel.similar.loading')}
          </Text>
        </Flex>
      </Box>
    );
  }

  // Once it resolves, a movement with nothing similar shows no section at all —
  // no header, no empty state.
  if (!similar || similar.ids.length === 0) {
    return null;
  }

  const line = similarLine(similar, t);
  const groupProps = { categories, allTags, onSelectMovement };
  return (
    <Box px={{ base: 2, md: 4 }} pt={4} pb={6}>
      <Text fontSize="sm" fontWeight="bold" color="redesign.heading">
        {t('detailPanel.similar.title')}
      </Text>
      {line && (
        <Text fontSize="sm" color="redesign.text.muted" mt={1}>
          {line}
        </Text>
      )}
      <SimilarGroup
        label={t('detailPanel.similar.sameCharge', { count: similar.same_charge_total })}
        filters={sameChargeFilters}
        more={similar.same_charge_total - similar.same_charge_ids.length}
        {...groupProps}
      />
      <SimilarGroup
        label={t('detailPanel.similar.otherVisits', { count: similar.other_total })}
        filters={otherFilters}
        more={similar.other_total - similar.other_ids.length}
        {...groupProps}
      />
    </Box>
  );
}


/**
 * One movement: read, edit, ignore or delete it, or (without `movementId`)
 * create one. `layout='panel'` (the default) is the desktop side panel;
 * `layout='phone'` draws the same state and handlers as the phone's
 * full-height movement sheet, open while `isOpen`.
 */
export default function MovementDetailPanel({ movementId, initialMovement, onClose, onMovementUpdated, onMovementDeleted, onSplitClick, onSelectMovement, layout = 'panel', isOpen = true, phoneHomeCurrency }) {
  const isCreateMode = !movementId;
  const { alerts: alertPrefs, homeCurrency } = usePreferences();
  // With Goals off the Goal field is not rendered and goal_id is never sent,
  // so saving a movement can't detach it from a goal someone else attached.
  const goalsEnabled = useGoalsEnabled();
  const [movement, setMovement] = useState(null);
  // MI-318 — the same converted value the list row shows, from the same map, so
  // the two can never disagree. Falls back to the original when nothing is being
  // converted, and `isConverted` is what decides whether the line renders.
  const converted = useDisplayAmount(movement);
  const [loading, setLoading] = useState(false);
  // The dropdown lists load on their own request. Tracked separately so the body
  // can wait for them too — otherwise Account / Category render empty and fill in
  // a beat later (MI-311).
  const [dropdownsLoading, setDropdownsLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [goals, setGoals] = useState([]);
  const [goalError, setGoalError] = useState(null);
  // The movement as edited, the payload built from it, and the exchange rule:
  // shared with the phone's quick add.
  const {
    draft: editedMovement,
    setDraft: setEditedMovement,
    resetDraft,
    handleFieldChange,
    mergeParsed,
    buildMovementPayload,
    showExchangeRow,
    amountValue,
  } = useMovementDraft({ accounts });
  const [isEditingValueTarget, setIsEditingValueTarget] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isIgnoring, setIsIgnoring] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // MI-315 — every way of ignoring asks, optionally, why: the menu's Ignore,
  // a Delete that turns into an ignore, and the phone's sheet. One answer.
  const [showIgnoreDialog, setShowIgnoreDialog] = useState(false);
  const [ignoreReason, setIgnoreReason] = useState('');
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [showMissingFieldsDialog, setShowMissingFieldsDialog] = useState(false);
  // AI mode state — default ON in create mode; user can toggle to form
  const [aiMode, setAiMode] = useState(true);
  const [aiText, setAiText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  // Refused over the AI budget (MI-354): the box then points to the form.
  const [aiOverBudget, setAiOverBudget] = useState(false);
  const toast = useToast();
  const { t } = useTranslation('movements');

  // Fetch movement data or initialize empty template for create mode
  useEffect(() => {
    if (movementId) {
      // MI-311: the list already holds this movement — /movements/search and
      // /movements/{id} run the same select_movement_shape + serialize_movement,
      // so the row is the same payload the detail call would return. When the
      // caller hands it over, use it and skip the round trip; callers that only
      // have an id (e.g. the "similar movements" links) still fetch.
      if (initialMovement && initialMovement.id === movementId) {
        setMovement(initialMovement);
        setEditedMovement(initialMovement);
        setError(null);
      } else {
        fetchMovementData();
      }
    } else {
      const today = todayLocalISO();
      const emptyMovement = {
        type: 'Expenses',
        date: today,
        amount: 0,
        note: '',
        description: '',
        currency: 'COP',
        account_id: null,
        category_id: null,
        from_account_id: null,
        to_account_id: null,
        tag_movements: [],
        currency_exchange: null,
      };
      setMovement(emptyMovement);
      resetDraft(emptyMovement);
      setAiMode(true);
      setAiText('');
      setAiError(null);
      setAiOverBudget(false);
    }
    fetchDropdownData();
  }, [movementId, resetDraft, setEditedMovement]);

  useEffect(() => {
    setGoalError(null);
    // The phone shows the goal as read-only text, so it needs no list.
    if (!goalsEnabled || !movementId || layout === 'phone') return;
    fetchGoals().then((list) => setGoals(list || [])).catch(() => {});
  }, [goalsEnabled, movementId, layout]);

  const fetchMovementData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMovementById(movementId);
      setMovement(data);
      setEditedMovement(data);
    } catch (err) {
      console.error('Error fetching movement:', err);
      setError(t('detailPanel.loadError'));
      toast({
        title: t('detailPanel.toasts.loadFailed.title'),
        description: t('detailPanel.toasts.loadFailed.description'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    setDropdownsLoading(true);
    try {
      // Shared across every panel open (MI-311) — see getReferenceData. Only
      // the first open in a workspace pays for the four requests.
      const { accounts, categories, currencies, tags } = await getReferenceData();
      setAccounts(accounts);
      setCategories(categories);
      setCurrencies(currencies);
      setAvailableTags(tags);
    } catch (err) {
      console.error('Error fetching dropdown data:', err);
    } finally {
      setDropdownsLoading(false);
    }
  };

  const haveTagsChanged = () => {
    const originalTagIds = (movement.tag_movements || [])
      .map(tm => tm.tag.id)
      .sort((a, b) => a - b);

    const currentTagIds = (editedMovement.tag_movements || [])
      .map(tm => tm.tag.id)
      .sort((a, b) => a - b);

    return JSON.stringify(originalTagIds) !== JSON.stringify(currentTagIds);
  };

  // Normalize an exchange to a comparable key; an empty/missing exchange
  // (no target currency and no target value) counts as "no exchange" so the
  // Save button only appears once there's something worth persisting.
  const normalizeExchange = (ex) => {
    if (!ex) return null;
    const currencyTarget = ex.currency_target || '';
    const valueTarget =
      ex.value_target === null || ex.value_target === undefined || ex.value_target === ''
        ? ''
        : String(ex.value_target);
    if (!currencyTarget && !valueTarget) return null;
    return `${currencyTarget}|${valueTarget}`;
  };

  const hasExchangeChanged = () =>
    normalizeExchange(editedMovement.currency_exchange) !==
    normalizeExchange(movement.currency_exchange);

  const hasGoalChanged = () =>
    goalsEnabled && (editedMovement.goal?.id ?? null) !== (movement.goal?.id ?? null);

  const hasTradeChanged = () => {
    const edited = tradeFields(editedMovement);
    const saved = tradeFields(movement);
    return edited.quantity !== saved.quantity || edited.unitPrice !== saved.unitPrice;
  };

  // An archived goal isn't offered, but a movement already in one still shows it.
  const goalOptions = useMemo(() => {
    const options = goals.map(({ id, name, color }) => ({ id, name, color }));
    const current = movement?.goal;
    if (current && !options.some((o) => o.id === current.id)) {
      options.push({ id: current.id, name: current.name, color: current.color });
    }
    return options;
  }, [goals, movement?.goal]);

  const hasChanges = !isCreateMode && movement && editedMovement && (
    editedMovement.type !== movement.type ||
    editedMovement.date !== movement.date ||
    toAmount(editedMovement.amount) !== toAmount(movement.amount) ||
    editedMovement.note !== movement.note ||
    editedMovement.description !== movement.description ||
    editedMovement.currency !== movement.currency ||
    editedMovement.account_id !== movement.account_id ||
    editedMovement.category_id !== movement.category_id ||
    editedMovement.from_account_id !== movement.from_account_id ||
    editedMovement.to_account_id !== movement.to_account_id ||
    haveTagsChanged() ||
    hasGoalChanged() ||
    hasExchangeChanged() ||
    hasTradeChanged()
  );

  const handleDiscard = () => {
    setEditedMovement({ ...movement });
  };

  // Every active review alert, as the sentence that explains it, shown
  // together in one banner at the top of the panel (never interleaved with
  // the fields). The panel judges the account and direction sentences over
  // the edited accounts, so the banner guides the fix and clears the moment
  // the user picks correctly; the phone sheet judges the saved movement, so
  // its card changes only once a fix is saved.
  const readTwiceFileId = useReadTwiceStatement(isCreateMode ? null : movement);
  const detailAlerts = useMemo(() => {
    if (isCreateMode || !movement) return [];
    return movementAlertReasons(movement, {
      accounts,
      t,
      prefs: alertPrefs,
      edited: layout === 'phone' ? movement : editedMovement,
      readTwiceFileId,
    });
  }, [isCreateMode, movement, editedMovement, accounts, alertPrefs, layout, t, readTwiceFileId]);
  // A warning a statement read twice raises is fixed in that statement's
  // checkup; opened from that checkup's own list, closing is the way back.
  const navigate = useNavigate();
  const location = useLocation();
  const fixInStatement = (fileId) => {
    const target = `${CHECKUP_PATH}?statement=${fileId}`;
    if (`${location.pathname}${location.search}` !== target) navigate(target);
  };

  const doUpdate = async () => {
    setUpdating(true);
    try {
      const payload = buildMovementPayload();

      if (haveTagsChanged()) {
        payload.tag_ids = (editedMovement.tag_movements || []).map(tm => tm.tag.id);
      }

      // Only sent when changed: an absent goal_id leaves the goal alone, null detaches.
      if (hasGoalChanged()) {
        payload.goal_id = editedMovement.goal?.id ?? null;
      }

      await updateMovement(movementId, payload);
      setGoalError(null);

      toast({ title: t('detailPanel.toasts.updateSuccess.title'), description: t('detailPanel.toasts.updateSuccess.description'), status: 'success', duration: 3000, isClosable: true });
      // Keep the panel open on the same movement: re-sync its baseline to the
      // saved server state (clears hasChanges, shows fresh data) and refresh
      // the list. Do NOT call onClose() — closing would "lose" the movement.
      const refreshed = await fetchMovementById(movementId);
      setMovement(refreshed);
      setEditedMovement(refreshed);
      // Hands the list what it would otherwise fetch a second time.
      if (onMovementUpdated) onMovementUpdated(refreshed);
    } catch (err) {
      console.error('Error updating movement:', err);
      // A goal the movement can't join says why under the Goal field instead.
      const goalReason = err.response?.data?.details?.reason;
      if (hasGoalChanged() && goalReason) {
        setGoalError(goalReason);
        return;
      }
      const isTransferError = err.response?.status === 400 && err.response?.data?.detail?.includes('Transfer requires at least one account');
      toast({
        title: isTransferError ? t('detailPanel.toasts.transferError.title') : t('detailPanel.toasts.updateError.title'),
        description: isTransferError ? t('detailPanel.toasts.transferError.description') : t('detailPanel.toasts.updateError.description'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleAiSubmit = async () => {
    setAiLoading(true);
    setAiError(null);
    setAiOverBudget(false);
    try {
      const parsed = await parseMovement(aiText);
      // Only the non-null keys. Submitting the box replaces the form, so what
      // was typed there after "Use form" is written over, as it always was.
      mergeParsed(parsed, { respectTouched: false });
      setAiMode(false);
    } catch (err) {
      // A 4xx's detail is written for people ("Text is required"); a 5xx's
      // is an internal error, so it gets the fallback, as does no response.
      const detail = err.response?.status < 500 ? err.response?.data?.detail : null;
      setAiError(typeof detail === 'string' && detail ? detail : t('detailPanel.aiInput.errorFallback'));
      setAiOverBudget(isAiBudgetRefusal(err));
    } finally {
      setAiLoading(false);
    }
  };

  const doCreate = async () => {
    setUpdating(true);
    try {
      const payload = buildMovementPayload();

      const tagIds = (editedMovement.tag_movements || []).map(tm => tm.tag.id);
      if (tagIds.length > 0) {
        payload.tag_ids = tagIds;
      }

      await postMovement(payload);

      toast({ title: t('detailPanel.toasts.createSuccess.title'), description: t('detailPanel.toasts.createSuccess.description'), status: 'success', duration: 3000, isClosable: true });
      if (onMovementUpdated) onMovementUpdated();
      if (onClose) onClose();
    } catch (err) {
      const backendMessage = err.response?.data?.detail;
      toast({
        title: t('detailPanel.toasts.createError.title'),
        description: backendMessage || t('detailPanel.toasts.createError.description'),
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleSave = async () => {
    if (!editedMovement.date || editedMovement.amount === '' || editedMovement.amount === null || !editedMovement.note || !editedMovement.type) {
      toast({ title: t('detailPanel.toasts.missingFields.title'), description: t('detailPanel.toasts.missingFields.description'), status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (getMissingFieldsMessage(editedMovement)) {
      setShowMissingFieldsDialog(true);
      return;
    }
    if (isCreateMode) {
      await doCreate();
    } else {
      await doUpdate();
    }
  };

  const handleAddTag = (selectedTag) => {
    const alreadyAdded = editedMovement.tag_movements?.some(
      tm => tm.tag.id === selectedTag.id
    );

    if (alreadyAdded) {
      toast({
        title: t('detailPanel.toasts.tagAlreadyAdded'),
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    const newTagMovement = {
      id: Date.now(),
      tag_id: selectedTag.id,
      movement_id: movementId || null,
      tag: {
        id: selectedTag.id,
        name: selectedTag.name
      }
    };

    setEditedMovement(prev => ({
      ...prev,
      tag_movements: [...(prev.tag_movements || []), newTagMovement]
    }));
  };

  const handleRemoveTag = (tagId) => {
    setEditedMovement(prev => ({
      ...prev,
      tag_movements: prev.tag_movements.filter(tm => tm.tag.id !== tagId)
    }));
  };

  const handleDeleteClick = async () => {
    setIsDeleting(true);
    setIgnoreReason('');
    try {
      const impact = await deleteMovement(movementId, true);
      console.log('Delete impact:', impact);
      setDeleteImpact(impact);
      setShowDeleteDialog(true);
    } catch (err) {
      // 409 means the movement has dependencies — show info in the dialog anyway
      if (err.response?.status === 409) {
        setShowDeleteDialog(true);
      } else {
        console.error('Error checking delete impact:', err);
        toast({
          title: t('detailPanel.toasts.deleteImpactError.title'),
          description: err.response?.data?.detail || t('detailPanel.toasts.deleteImpactError.description'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Ignoring straight from the menu, without going through Delete. Unlike a
  // delete it is reversible, so the dialog asks one optional thing — why — and
  // Restore sits in the same menu. `ignoreReason` is blank when none was given.
  const handleIgnoreClick = async () => {
    setIsIgnoring(true);
    try {
      await ignoreMovement(movementId, ignoreReason);
      toast({
        title: t('detailPanel.toasts.ignoreSuccess.title'),
        description: t('detailPanel.toasts.ignoreSuccess.description'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      // Refresh the panel itself as well as the list — the banner, the struck
      // amount and the Restore action all read `movement.is_ignored`, which
      // the list refresh alone would leave stale.
      await fetchMovementData();
      if (onMovementUpdated) onMovementUpdated();
    } catch (err) {
      console.error('Error ignoring movement:', err);
      toast({
        title: t('detailPanel.toasts.ignoreError.title'),
        description: err.response?.data?.detail || t('detailPanel.toasts.ignoreError.description'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsIgnoring(false);
    }
  };

  // Restoring an ignored movement puts it back into every total. The
  // panel stays open — the row simply stops being dimmed — because the user is
  // usually looking at it to check it came back correctly.
  const handleRestoreClick = async () => {
    setIsRestoring(true);
    try {
      await restoreMovement(movementId);
      toast({
        title: t('detailPanel.toasts.restoreSuccess.title'),
        description: t('detailPanel.toasts.restoreSuccess.description'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await fetchMovementData();
      if (onMovementUpdated) onMovementUpdated();
    } catch (err) {
      console.error('Error restoring movement:', err);
      toast({
        title: t('detailPanel.toasts.restoreError.title'),
        description: err.response?.data?.detail || t('detailPanel.toasts.restoreError.description'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRestoring(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    const wasIgnore = deleteImpact?.will_ignore;
    try {
      // A delete the dialog announced as an ignore goes through the ignore
      // endpoint: the same write, and the one that takes the reason (MI-315).
      if (wasIgnore) await ignoreMovement(movementId, ignoreReason);
      else await deleteMovement(movementId);
      toast({
        title: wasIgnore
          ? t('detailPanel.toasts.ignoreSuccess.title')
          : t('detailPanel.toasts.deleteSuccess.title'),
        description: wasIgnore
          ? t('detailPanel.toasts.ignoreSuccess.description')
          : t('detailPanel.toasts.deleteSuccess.description'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      if (onMovementDeleted) {
        onMovementDeleted();
      } else if (onMovementUpdated) {
        onMovementUpdated();
      }
      onClose();
    } catch (err) {
      console.error('Error deleting movement:', err);
      toast({
        title: t('detailPanel.toasts.deleteError.title'),
        description: err.response?.data?.detail || t('detailPanel.toasts.deleteError.description'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteImpact(null);
    }
  };

  // MI-311: hold the whole body until every piece is in. Painting as soon as the
  // movement arrived meant Account / Category / Origin filled in one at a time —
  // and switching movements briefly showed the previous one's values, because the
  // fetch effect only runs after that render.
  const isStaleMovement = Boolean(movementId) && movement?.id !== movementId;
  const isPanelLoading = loading || dropdownsLoading || isStaleMovement;

  // An ignored movement is frozen: every field is read-only until it is
  // restored. The backend rejects the write too, so this is the friendly half
  // of the rule rather than the whole of it.
  const fieldsLocked = !!movement?.is_ignored;

  // A Transfer into or out of a holding, or Income on one, carries units and
  // a price per unit (a buy, a sell, units received). The price falls back to
  // the amount over the units, which is what the server uses when none is typed.
  const editedTradeSide = editedMovement ? tradeSide(editedMovement, accounts) : null;
  const editedTrade = tradeFields(editedMovement);
  const tradeValue = (key, saved) => (editedMovement && key in editedMovement ? editedMovement[key] : (saved ?? ''));
  const derivedUnitPrice = editedTrade.quantity > 0 && amountValue > 0 ? amountValue / editedTrade.quantity : null;

  // MI-336 — the panel can be dragged narrow, so its layout answers to its own
  // width rather than to the window's.
  const [panelRef, compact] = useNarrowerThan(COMPACT_WIDTH);
  // The phone sheet draws the similar movements from the panel's state; the
  // desktop section looks them up on its own.
  const phoneSimilar = useSimilarMovements(layout === 'phone' ? movementId : null);

  if (layout === 'phone') {
    return (
      <PhoneMovementDetail
        isOpen={isOpen}
        onClose={onClose}
        loading={isPanelLoading}
        error={error}
        movement={movement}
        draft={editedMovement}
        accounts={accounts}
        categories={categories}
        currencies={currencies}
        tags={availableTags}
        reasons={detailAlerts}
        onFixInStatement={fixInStatement}
        converted={converted}
        similar={phoneSimilar}
        goalsEnabled={goalsEnabled}
        homeCurrency={phoneHomeCurrency || homeCurrency}
        handleFieldChange={handleFieldChange}
        setDraft={setEditedMovement}
        hasChanges={Boolean(hasChanges)}
        onDiscard={handleDiscard}
        onSave={handleSave}
        saving={updating}
        missingOpen={showMissingFieldsDialog}
        onMissingCancel={() => setShowMissingFieldsDialog(false)}
        onMissingConfirm={() => { setShowMissingFieldsDialog(false); doUpdate(); }}
        onIgnore={handleIgnoreClick}
        ignoreReason={ignoreReason}
        onIgnoreReasonChange={setIgnoreReason}
        onRestore={handleRestoreClick}
        ignoring={isIgnoring}
        restoring={isRestoring}
        onDeleteCheck={handleDeleteClick}
        deleteChecked={showDeleteDialog}
        deleteImpact={deleteImpact}
        deleting={isDeleting}
        onDeleteCancel={() => { setShowDeleteDialog(false); setDeleteImpact(null); }}
        onDeleteConfirm={handleDeleteConfirm}
      />
    );
  }

  return (
    <Box
      ref={panelRef}
      h="100%"
      bg="redesign.bg.surface"
      borderLeft="1px solid"
      borderColor="redesign.border"
      display="flex"
      flexDirection="column"
    >
      {/* Header row: Type dropdown on left, icons on right.
          MI-311: sits outside the scroll container so the close button stays
          reachable at mobile width — where the panel covers the whole screen —
          no matter how far the body is scrolled or whether it's still loading. */}
      <Flex
        justify="space-between"
        align="center"
        px={{ base: 2, md: 4 }}
        py={2}
        borderBottom="1px solid"
        borderColor="redesign.border"
        flexShrink={0}
      >
        {/* Type dropdown — hidden in AI create mode and until the movement
            lands. Frozen (plain text, no popover) while the movement is
            ignored, since type is an editable field like any other. */}
        {(aiMode && isCreateMode) || !editedMovement ? (
          <Box />
        ) : fieldsLocked ? (
          <Text
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="600"
            color={
              editedMovement.type === 'Income' ? 'redesign.amount.income' :
              editedMovement.type === 'Expenses' ? 'redesign.heading' :
              'redesign.accent.blue'
            }
            opacity={0.6}
          >
            {t(TYPE_OPTIONS.find(o => o.value === editedMovement.type)?.labelKey || 'types.expenses')}
          </Text>
        ) : (
          <Popover placement="bottom-start">
            {({ onClose: closeTypePopover }) => (
              <>
                <PopoverTrigger>
                  <Flex
                    align="center"
                    gap={1}
                    cursor="pointer"
                    _hover={{ opacity: 0.8 }}
                  >
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      fontWeight="600"
                      color={
                        editedMovement.type === 'Income' ? 'redesign.amount.income' :
                        editedMovement.type === 'Expenses' ? 'redesign.heading' :
                        'redesign.accent.blue'
                      }
                    >
                      {t(`types.${editedMovement.type === 'Income' ? 'income' : editedMovement.type === 'Expenses' ? 'expenses' : 'transfer'}`)}
                    </Text>
                    <Box
                      as="svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      w="14px"
                      h="14px"
                      color="redesign.text.muted"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </Box>
                  </Flex>
                </PopoverTrigger>
                <PopoverContent w="180px" boxShadow="lg" borderRadius="lg">
                  <PopoverBody p={1}>
                    {TYPE_OPTIONS.map(opt => (
                      <Flex
                        key={opt.value}
                        px={3}
                        py={2}
                        cursor="pointer"
                        borderRadius="md"
                        bg={opt.value === editedMovement.type ? "redesign.bg.card" : "transparent"}
                        _hover={{ bg: "redesign.bg.hover" }}
                        onClick={() => {
                          handleFieldChange('type', opt.value);
                          closeTypePopover();
                        }}
                        align="center"
                      >
                        <Text
                          fontSize="sm"
                          fontWeight={opt.value === editedMovement.type ? "600" : "400"}
                          color={
                            opt.value === 'Income' ? 'redesign.amount.income' :
                            opt.value === 'Expenses' ? 'redesign.heading' :
                            'redesign.accent.blue'
                          }
                        >
                          {t(opt.labelKey)}
                        </Text>
                      </Flex>
                    ))}
                  </PopoverBody>
                </PopoverContent>
              </>
            )}
          </Popover>
        )}

        <Flex align="center" gap={1}>
          {/* AI toggle - only in create mode */}
          {isCreateMode && (
            <Tooltip label={aiMode ? t('detailPanel.tooltips.useForm') : t('detailPanel.tooltips.useAi')}>
              <IconButton
                icon={
                  <Box as="svg" viewBox="0 0 24 24" w="22px" h="22px">
                    <defs>
                      <linearGradient id="ai-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="var(--chakra-colors-redesign-accent-assets)" />
                        <stop offset="100%" stopColor="var(--chakra-colors-redesign-accent-blue)" />
                      </linearGradient>
                    </defs>
                    <path fill="url(#ai-gradient)" d="M10 2C10 2 7 8.5 2 10c5 1.5 8 8 8 8s3-6.5 8-8c-5-1.5-8-8-8-8z" />
                    <path fill="url(#ai-gradient)" d="M18 14c0 0-1 2-2.5 2.5 1.5.5 2.5 2.5 2.5 2.5s1-2 2.5-2.5c-1.5-.5-2.5-2.5-2.5-2.5z" />
                  </Box>
                }
                size="xs"
                variant="ghost"
                aria-label={aiMode ? t('detailPanel.tooltips.useForm') : t('detailPanel.tooltips.useAi')}
                opacity={aiMode ? 1 : 0.5}
                _hover={{ opacity: 0.8 }}
                onClick={() => setAiMode(!aiMode)}
              />
            </Tooltip>
          )}
          {/* More Options Menu - only in edit mode, once the movement is loaded */}
          {!isCreateMode && movement && !isPanelLoading && (
            <Popover placement="bottom-end">
              {({ onClose: closePopover }) => (
                <>
                  <PopoverTrigger>
                    <IconButton
                      icon={<Text fontSize="lg">⋯</Text>}
                      size="sm"
                      variant="ghost"
                      aria-label={t('detailPanel.aria.moreOptions')}
                      color="redesign.text.muted"
                      _hover={{ color: "redesign.text" }}
                    />
                  </PopoverTrigger>
                  <PopoverContent w="160px" boxShadow="lg" borderRadius="lg">
                    <PopoverBody p={2}>
                      {onSplitClick && (
                        <Button
                          size="sm"
                          width="full"
                          variant="outline"
                          colorScheme="gray"
                          justifyContent="flex-start"
                          leftIcon={<Icon as={LuSplit} boxSize={4} />}
                          onClick={() => {
                            closePopover();
                            onSplitClick(movement);
                          }}
                          mb={1}
                        >
                          {t('detailPanel.buttons.split')}
                        </Button>
                      )}
                      {/* An ignored movement cannot be ignored or
                          deleted again — Restore is the only move from here. */}
                      {movement?.is_ignored ? (
                        <Button
                          colorScheme="green"
                          size="sm"
                          width="full"
                          justifyContent="flex-start"
                          leftIcon={<Icon as={LuRotateCcw} boxSize={4} />}
                          onClick={() => {
                            closePopover();
                            handleRestoreClick();
                          }}
                          isLoading={isRestoring}
                        >
                          {t('detailPanel.buttons.restore')}
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            width="full"
                            variant="outline"
                            colorScheme="gray"
                            justifyContent="flex-start"
                            leftIcon={<NotAllowedIcon />}
                            onClick={() => {
                              closePopover();
                              setIgnoreReason('');
                              setShowIgnoreDialog(true);
                            }}
                            isLoading={isIgnoring}
                            mb={1}
                          >
                            {t('detailPanel.buttons.ignore')}
                          </Button>
                          <Button
                            colorScheme="red"
                            size="sm"
                            width="full"
                            justifyContent="flex-start"
                            leftIcon={<DeleteIcon />}
                            onClick={() => {
                              closePopover();
                              handleDeleteClick();
                            }}
                            isLoading={isDeleting}
                          >
                            {t('detailPanel.buttons.delete')}
                          </Button>
                        </>
                      )}
                    </PopoverBody>
                  </PopoverContent>
                </>
              )}
            </Popover>
          )}
          <IconButton
            icon={<CloseIcon boxSize="10px" />}
            size="xs"
            variant="ghost"
            color="redesign.text.muted"
            _hover={{ color: "redesign.text", bg: "redesign.bg.card" }}
            onClick={onClose}
            aria-label={t('detailPanel.aria.closePanel')}
          />
        </Flex>
      </Flex>

      {/* Content - Scrollable */}
      <Box flex="1" overflowY="auto">
        {isPanelLoading && !error && (
          <Flex justify="center" align="center" py={12}>
            <Spinner size="xl" />
          </Flex>
        )}

        {error && (
          <Text color="redesign.accent.red" textAlign="center" py={12}>
            {error}
          </Text>
        )}


        {!isPanelLoading && !error && movement && editedMovement && (
          <Box>

            {/* An ignored movement is still fully editable, so the panel needs
                to say plainly why its amount counts for nothing. The banner
                carries the undo, which is the action a user opening an ignored
                row is most likely looking for. */}
            {movement.is_ignored && (
              <Flex
                align="center"
                justify="space-between"
                gap={2}
                px={{ base: 2, md: 4 }}
                py={2}
                bg="rgba(148, 163, 184, 0.16)"
                borderBottom="1px solid"
                borderColor="redesign.border"
              >
                <Flex align="center" gap={2} minW={0}>
                  <Text fontSize="sm">🚫</Text>
                  <Box minW={0}>
                    <Text fontSize="xs" fontWeight="700" color="redesign.text" lineHeight="1.3">
                      {t('detailPanel.ignoredBanner.title')}
                    </Text>
                    <Text fontSize="2xs" color="redesign.text.muted" lineHeight="1.3">
                      {t('detailPanel.ignoredBanner.description')}
                    </Text>
                    {movement.ignored_reason && (
                      <Text fontSize="xs" color="redesign.text" lineHeight="1.4" mt={1} whiteSpace="pre-wrap">
                        {t('detailPanel.ignoredBanner.reason', { reason: movement.ignored_reason })}
                      </Text>
                    )}
                  </Box>
                </Flex>
                <Button
                  size="xs"
                  colorScheme="green"
                  variant="outline"
                  flexShrink={0}
                  onClick={handleRestoreClick}
                  isLoading={isRestoring}
                >
                  {t('detailPanel.buttons.restore')}
                </Button>
              </Flex>
            )}

            {/* AI mode: textarea for natural language input */}
            {aiMode && isCreateMode ? (
              <Box px={{ base: 2, md: 4 }} pt={{ base: 2, md: 4 }}>
                <Textarea
                  value={aiText}
                  onChange={(e) => setAiText(e.target.value)}
                  placeholder={t('detailPanel.aiInput.placeholder')}
                  rows={4}
                  mb={3}
                  isDisabled={aiLoading}
                  fontSize={{ base: "sm", md: "md" }}
                />
                {aiError && <Text color="redesign.accent.red" fontSize="sm" mb={aiOverBudget ? 1 : 2}>{aiError}</Text>}
                {aiOverBudget && <Text color="redesign.text.muted" fontSize="sm" mb={2}>{t('common:aiBudget.useForm')}</Text>}
              </Box>
            ) : (
            <Box
              px={{ base: 2, md: 4 }}
              pt={{ base: 2, md: 4 }}
              // One gate on the whole field region rather than a prop on each
              // control: the fields are a mix of inputs, popovers and inline
              // pickers, and a per-control flag is the kind of list that grows
              // a hole the next time a field is added.
              pointerEvents={fieldsLocked ? 'none' : undefined}
              opacity={fieldsLocked ? 0.6 : 1}
              aria-disabled={fieldsLocked || undefined}
            >
                {/* Date */}
                <CalendarPicker
                  value={editedMovement.date}
                  onChange={(date) => handleFieldChange('date', date)}
                >
                  <Box
                    display="inline-block"
                    mb="4px"
                    cursor="pointer"
                    borderRadius="md"
                    _hover={{ bg: "redesign.bg.card" }}
                    transition="background 0.15s"
                  >
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color="redesign.text.muted"
                      py="2px"
                    >
                      {formatDateLong(editedMovement.date)}
                    </Text>
                  </Box>
                </CalendarPicker>

                {/* Note + amount on one line — until the panel is too narrow
                    to hold a name and a seven-digit COP figure side by side,
                    where the amount drops under the name instead of squeezing
                    the name to nothing (MI-336). */}
                <Flex align="baseline" mb={1} flexWrap={compact ? "wrap" : "nowrap"} rowGap="2px">
                  <Input
                    value={editedMovement.note || ''}
                    onChange={(e) => handleFieldChange('note', e.target.value)}
                    isDisabled={fieldsLocked}
                    // MI-336 — smaller in a narrow panel: "lg"/"xl" was sized
                    // for the 500px default, and stays that size at 280px
                    // where a seven-digit COP figure has far less room to fit.
                    fontSize={compact ? "md" : { base: "lg", md: "xl" }}
                    fontWeight="600"
                    color="redesign.heading"
                    variant="unstyled"
                    placeholder={isCreateMode ? t('detailPanel.name.placeholderCreate') : t('detailPanel.name.placeholderEdit')}
                    _placeholder={{ color: "redesign.text.faint" }}
                    lineHeight="1.2"
                    letterSpacing="-0.02em"
                    h="auto"
                    py={0}
                    // Compact: the name takes the whole line so the amount
                    // lands under it, instead of both being squeezed until
                    // neither is readable.
                    flex={compact ? "1 0 100%" : "1"}
                    mr={compact ? 0 : 2}
                  />
                  {/* Currency + Amount */}
                  <Flex align="baseline" flexShrink={0} whiteSpace="nowrap">
                    <CurrencyPicker
                      value={editedMovement.currency}
                      currencies={currencies}
                      onChange={(name) => handleFieldChange('currency', name)}
                      placement="bottom-end"
                    >
                      <Text
                        as="span"
                        fontSize="sm"
                        fontWeight="600"
                        color="redesign.text.muted"
                        cursor="pointer"
                        _hover={{ color: "redesign.text" }}
                      >
                        {editedMovement.currency}
                      </Text>
                    </CurrencyPicker>
                    <Input
                      type="text"
                      inputMode="decimal"
                      isDisabled={fieldsLocked}
                      // A stored amount is a number; what the user types stays
                      // text, read by the amount parser (either separator).
                      value={typeof editedMovement.amount === 'number' ? amountToText(editedMovement.amount) : editedMovement.amount}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9.,]/g, '');
                        handleFieldChange('amount', val);
                      }}
                      fontSize={compact ? "md" : { base: "lg", md: "xl" }}
                      fontWeight="700"
                      color={
                        editedMovement.type === 'Income' ? 'redesign.amount.income' :
                        editedMovement.type === 'Expenses' ? 'redesign.amount.expense' :
                        'redesign.amount.neutral'
                      }
                      variant="unstyled"
                      textAlign="right"
                      h="auto"
                      py={0}
                      pl={0}
                      lineHeight="1.2"
                      // Struck through while ignored, matching the list row —
                      // this is the figure that no longer reaches any total.
                      textDecoration={movement?.is_ignored ? 'line-through' : 'none'}
                      w={`${Math.max(String(editedMovement.amount || '0').length, 1) * 0.65 + 0.5}em`}
                    />
                  </Flex>
                </Flex>

                {/* MI-318 — the converted figure, under the real one. The
                    amount above stays editable and in the movement's own
                    currency: that is what was actually spent, and what a save
                    would write. This line is read-only context, and says so
                    with the same `≈` the list rows use. */}
                {converted.isConverted && (
                  <Flex justify="flex-end" mt={0.5}>
                    <RateSourceTooltip sources={converted.rateSources}>
                      <Text fontSize="xs" color="redesign.text.muted" fontWeight="500">
                        {`≈ ${converted.currency} ${formatMoney(converted.amount, converted.currency, true)}`}
                      </Text>
                    </RateSourceTooltip>
                  </Flex>
                )}

                {/* MI-282 Rev2: all review alerts grouped here, at the top —
                    never interleaved with the field rows below. */}
                {detailAlerts.length > 0 && (
                  <Flex direction="column" gap={1.5} bg="rgba(239, 68, 68, 0.10)" px={3} py={2.5} borderRadius="md" mt={2}>
                    {detailAlerts.map(({ text: msg, fixFileId }, i) => (
                      <Flex key={i} align="flex-start" gap={2}>
                        <WarningIcon color="redesign.accent.red" boxSize={3.5} flexShrink={0} mt="2px" />
                        <Box>
                          <Text fontSize="xs" color="redesign.accent.red" fontWeight="500">{msg}</Text>
                          {fixFileId != null && (
                            <Button mt={2} size="xs" colorScheme="blue" onClick={() => fixInStatement(fixFileId)}>
                              {t('detailPanel.alerts.fixInStatement')}
                            </Button>
                          )}
                        </Box>
                      </Flex>
                    ))}
                  </Flex>
                )}

                {/* Fields section */}
                <Box mt={4} borderTop="1px solid" borderColor="redesign.border" pt={2}>
                  {editedMovement.type === 'Transfer' ? (
                    <>
                      <Flex py="8px" align="center" flexWrap="wrap" rowGap="2px">
                        <FieldLabel compact={compact}>{t('detailPanel.fields.from')}</FieldLabel>
                        <CustomDropdown
                          value={editedMovement.from_account_id}
                          options={accounts}
                          onChange={(option) => handleFieldChange('from_account_id', option ? option.id : null)}
                          showInactiveIndicator={true}
                          isClearable
                        />
                      </Flex>
                      <Flex py="8px" align="center" flexWrap="wrap" rowGap="2px">
                        <FieldLabel compact={compact}>{t('detailPanel.fields.to')}</FieldLabel>
                        <CustomDropdown
                          value={editedMovement.to_account_id}
                          options={accounts}
                          onChange={(option) => handleFieldChange('to_account_id', option ? option.id : null)}
                          showInactiveIndicator={true}
                          isClearable
                        />
                      </Flex>
                      {showExchangeRow && !editedMovement.currency_exchange && (
                        <Flex py="8px" align="center" flexWrap="wrap" rowGap="2px">
                          <FieldLabel compact={compact}>{t('detailPanel.fields.exchange')}</FieldLabel>
                          <Flex
                            align="center"
                            justify="center"
                            w="28px"
                            h="28px"
                            borderRadius="full"
                            border="1px solid"
                            borderColor="redesign.border"
                            color="redesign.text.muted"
                            cursor="pointer"
                            _hover={{ borderColor: "redesign.border", color: "redesign.text.muted" }}
                            fontSize="md"
                            fontWeight="300"
                            aria-label={t('detailPanel.fields.exchangeAdd')}
                            onClick={() => setEditedMovement(prev => ({
                              ...prev,
                              currency_exchange: { currency_target: '', value_target: '' }
                            }))}
                          >+</Flex>
                        </Flex>
                      )}
                      {showExchangeRow && editedMovement.currency_exchange && (
                        <Flex py="8px" align="center" flexWrap="wrap" rowGap="2px">
                          <FieldLabel compact={compact}>{t('detailPanel.fields.exchange')}</FieldLabel>
                          <Text fontSize={{ base: "xs", md: "sm" }} color="redesign.text">
                            {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amountValue ?? 0)} {editedMovement.currency}
                            {" → "}
                            {isEditingValueTarget ? (
                              <Input
                                as="input"
                                type="number"
                                isDisabled={fieldsLocked}
                                value={editedMovement.currency_exchange.value_target}
                                onChange={(e) => setEditedMovement(prev => ({
                                  ...prev,
                                  currency_exchange: { ...prev.currency_exchange, value_target: e.target.value }
                                }))}
                                onBlur={() => setIsEditingValueTarget(false)}
                                autoFocus
                                display="inline"
                                fontSize={{ base: "xs", md: "sm" }}
                                variant="unstyled"
                                w="80px"
                              />
                            ) : (
                              <Text
                                as="span"
                                fontSize={{ base: "xs", md: "sm" }}
                                cursor="pointer"
                                _hover={{ color: "redesign.accent.blue" }}
                                onClick={() => setIsEditingValueTarget(true)}
                              >
                                {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(editedMovement.currency_exchange.value_target)}
                              </Text>
                            )}
                            {" "}
                            <CurrencyPicker
                              value={editedMovement.currency_exchange.currency_target}
                              currencies={currencies}
                              onChange={(name) => setEditedMovement(prev => ({
                                ...prev,
                                currency_exchange: { ...prev.currency_exchange, currency_target: name }
                              }))}
                            >
                              <Text as="span" fontSize={{ base: "xs", md: "sm" }} cursor="pointer" _hover={{ color: "redesign.accent.blue" }} color={editedMovement.currency_exchange.currency_target ? "redesign.text" : "redesign.text.muted"}>
                                {editedMovement.currency_exchange.currency_target || t('detailPanel.fields.exchangeCurrencyPlaceholder')}
                              </Text>
                            </CurrencyPicker>
                          </Text>
                        </Flex>
                      )}
                    </>
                  ) : (
                    <>
                      <Flex py="8px" align="center" flexWrap="wrap" rowGap="2px">
                        <FieldLabel compact={compact}>{t('detailPanel.fields.account')}</FieldLabel>
                        <CustomDropdown
                          value={editedMovement.account_id}
                          options={accounts}
                          onChange={(option) => handleFieldChange('account_id', option ? option.id : null)}
                          showInactiveIndicator={true}
                          isClearable
                        />
                      </Flex>
                      <Flex py="8px" align="center" flexWrap="wrap" rowGap="2px">
                        <FieldLabel compact={compact}>{t('detailPanel.fields.category')}</FieldLabel>
                        <CustomDropdown
                          value={editedMovement.category_id}
                          options={categories.filter(c => c.type === editedMovement.type)}
                          onChange={(option) => handleFieldChange('category_id', option ? option.id : null)}
                          defaultColor={DEFAULT_CATEGORY_COLOR}
                          isClearable
                        />
                      </Flex>
                    </>
                  )}

                  {editedTradeSide && (
                    <Flex py="8px" align="center" flexWrap="wrap" rowGap="2px">
                      <FieldLabel compact={compact}>{t('detailPanel.fields.units')}</FieldLabel>
                      <Flex align="center" gap={2} flex="1" minW={0} flexWrap="wrap" rowGap={1}>
                        <Input
                          type="number"
                          step="any"
                          min="0"
                          size="sm"
                          w="110px"
                          borderRadius="md"
                          isDisabled={fieldsLocked}
                          value={tradeValue('trade_quantity', editedMovement.trade?.quantity)}
                          onChange={(e) => handleFieldChange('trade_quantity', e.target.value)}
                          placeholder="0"
                          aria-label={t('detailPanel.fields.units')}
                        />
                        <Text fontSize="sm" color="redesign.text.muted">@</Text>
                        <Input
                          type="number"
                          step="any"
                          min="0"
                          size="sm"
                          w="120px"
                          borderRadius="md"
                          isDisabled={fieldsLocked}
                          value={tradeValue('trade_unit_price', editedMovement.trade?.unit_price)}
                          onChange={(e) => handleFieldChange('trade_unit_price', e.target.value)}
                          placeholder={derivedUnitPrice != null ? formatPrice(derivedUnitPrice) : t('detailPanel.fields.pricePerUnit')}
                          aria-label={t('detailPanel.fields.pricePerUnit')}
                          title={t('detailPanel.fields.pricePerUnit')}
                        />
                        <Tag size="sm" variant="subtle" borderRadius="full" colorScheme={editedTradeSide === 'sell' ? 'orange' : editedTradeSide === 'buy' ? 'blue' : 'green'}>
                          {t(`detailPanel.trade.${editedTradeSide}`)}
                        </Tag>
                      </Flex>
                    </Flex>
                  )}

                  {/* Goal — after Category, before Tags. Not part of the type
                      branches above, so a transfer can carry a goal too. */}
                  {goalsEnabled && !isCreateMode && (
                    <Box py="8px">
                      <Flex align="center" flexWrap="wrap" rowGap="2px">
                        <FieldLabel compact={compact}>{t('goals:field.label')}</FieldLabel>
                        <CustomDropdown
                          value={editedMovement.goal?.id ?? null}
                          options={goalOptions}
                          onChange={(option) => {
                            setGoalError(null);
                            const goal = option && (goals.find(g => g.id === option.id) || movement.goal);
                            handleFieldChange('goal', goal ? { id: goal.id, name: goal.name, color: goal.color, icon: goal.icon } : null);
                          }}
                          defaultColor="#3B82F6"
                          isClearable
                        />
                      </Flex>
                      {goalError ? (
                        <Text fontSize="xs" color="redesign.text.warn" mt={1}>
                          {t('goals:field.rejected', { reason: t(`goals:reasons.${goalError}`, { defaultValue: goalError }) })}
                        </Text>
                      ) : movement.goal && !hasGoalChanged() && movement.goal.effect !== 'none' && (
                        <Text fontSize="xs" color="redesign.text.muted" mt={1}>
                          {movement.goal.effect === 'invalid' ? (
                            t('goals:field.doesNotCount', {
                              reason: t(`goals:reasons.${movement.goal.reason}`, { defaultValue: movement.goal.reason }),
                            })
                          ) : (
                            <Trans
                              t={t}
                              i18nKey="goals:field.counts"
                              values={{
                                amount: `${movement.goal.effect === 'add' ? '+' : '−'} ${movement.currency} ${Number(movement.amount || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
                              }}
                              components={{ strong: <strong /> }}
                            />
                          )}
                        </Text>
                      )}
                    </Box>
                  )}

                  {/* Tags */}
                  <Flex py="8px" align="center" flexWrap="wrap" rowGap="2px">
                    <FieldLabel compact={compact}>{t('detailPanel.fields.tags')}</FieldLabel>
                    <Box flex="1" minW={0}>
                      <MultiSelectDropdown
                        selectedIds={(editedMovement.tag_movements || []).map(tm => tm.tag.id)}
                        options={availableTags}
                        onAdd={handleAddTag}
                        onRemove={handleRemoveTag}
                        placeholder={t('detailPanel.fields.tagsPlaceholder')}
                      />
                    </Box>
                  </Flex>

                  {/* Source File - only in edit mode */}
                  {!isCreateMode && (() => {
                    const sourceFiles = [...new Set(
                      movement.source_files
                        ?.map(sf => sf.filename)
                        .filter(Boolean)
                    )];
                    return sourceFiles.length > 0 ? (
                      <Flex py={{ base: 2, md: 3 }} align="center" flexWrap="wrap" rowGap="2px">
                        <FieldLabel compact={compact}>{t('detailPanel.fields.source')}</FieldLabel>
                        <SourceFilesPopover sourceFiles={sourceFiles} />
                      </Flex>
                    ) : null;
                  })()}

                  {/* Note */}
                  <Box mt={{ base: 2, md: 3 }} py={2} borderTop="1px solid" borderBottom="1px solid" borderColor="redesign.border">
                    <Input
                      value={editedMovement.description || ''}
                      onChange={(e) => handleFieldChange('description', e.target.value)}
                      isDisabled={fieldsLocked}
                      placeholder={t('detailPanel.description.placeholder')}
                      variant="unstyled"
                      fontSize={{ base: "sm", md: "md" }}
                      color="redesign.text"
                      _placeholder={{ color: "redesign.text.muted" }}
                      px={3}
                      py={2}
                      borderRadius="lg"
                      _hover={{ bg: "redesign.bg.hover" }}
                      _focus={{ bg: "redesign.bg.hover" }}
                      transition="background 0.15s"
                    />
                  </Box>
                </Box>

              {!isCreateMode && (
                <GroupSection
                  movement={movement}
                  categories={categories}
                  allTags={availableTags}
                  onSelectMovement={onSelectMovement}
                />
              )}
              {!isCreateMode && (
                <SimilarMovementsSection
                  movementId={movementId}
                  categories={categories}
                  allTags={availableTags}
                  onSelectMovement={onSelectMovement}
                />
              )}
            </Box>
            )}

            {/* Floating action bar */}
            {isCreateMode ? (
              <Flex
                position="sticky"
                bottom={0}
                px={{ base: 2, md: 4 }}
                py={3}
                bg="redesign.bg.surface"
                borderTop="1px solid"
                borderColor="redesign.border"
                gap={2}
                align="center"
                justify="flex-end"
                boxShadow="0 -2px 8px rgba(0,0,0,0.04)"
              >
                {aiMode ? (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      color="redesign.text.muted"
                      onClick={() => setAiMode(false)}
                      _hover={{ bg: "redesign.bg.card" }}
                    >
                      {t('detailPanel.buttons.useForm')}
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={handleAiSubmit}
                      isLoading={aiLoading}
                      isDisabled={!aiText.trim()}
                      px={6}
                    >
                      {t('detailPanel.buttons.submit')}
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={handleSave}
                    isLoading={updating}
                    px={6}
                  >
                    {t('detailPanel.buttons.create')}
                  </Button>
                )}
              </Flex>
            ) : null}
          </Box>
        )}
      </Box>

      {/* MI-315 — an edit's Save bar is pinned under the scroll area rather
          than inside it: inside, it sat wherever the content ended, which with
          a movement's similar ones listed was the end of that list. The create
          bar stays in the flow, beside the form or the text it submits. */}
      {!isCreateMode && hasChanges && !isPanelLoading && !error && (
        <Flex
          px={{ base: 2, md: 4 }}
          py={3}
          bg="redesign.bg.surface"
          borderTop="1px solid"
          borderColor="redesign.border"
          gap={2}
          align="center"
          justify="flex-end"
          boxShadow="0 -2px 8px rgba(0,0,0,0.04)"
        >
          <Button
            size="sm"
            variant="ghost"
            color="redesign.text.muted"
            onClick={handleDiscard}
            _hover={{ bg: "redesign.bg.card" }}
          >
            {t('detailPanel.buttons.discard')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={handleSave}
            isLoading={updating}
            px={6}
          >
            {t('detailPanel.buttons.save')}
          </Button>
        </Flex>
      )}

      <ConfirmationDialog
        isOpen={showIgnoreDialog}
        onClose={() => setShowIgnoreDialog(false)}
        onConfirm={async () => { await handleIgnoreClick(); setShowIgnoreDialog(false); }}
        title={t('detailPanel.ignoreDialog.title')}
        confirmText={t('detailPanel.ignoreDialog.confirm')}
        confirmColorScheme="gray"
        isLoading={isIgnoring}
      >
        <Box>
          <Text mb={3}>{t('detailPanel.ignoreDialog.message')}</Text>
          <IgnoreReasonField value={ignoreReason} onChange={setIgnoreReason} autoFocus />
        </Box>
      </ConfirmationDialog>

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => { setShowDeleteDialog(false); setDeleteImpact(null); }}
        onConfirm={handleDeleteConfirm}
        // The same button does two different things depending on
        // whether this movement is the last link to a bank transaction, so the
        // dialog has to say which one is about to happen.
        title={deleteImpact?.will_ignore
          ? t('detailPanel.deleteDialog.ignoreTitle')
          : t('detailPanel.deleteDialog.title')}
        confirmText={deleteImpact?.will_ignore
          ? t('detailPanel.deleteDialog.ignoreConfirm')
          : t('detailPanel.deleteDialog.confirm')}
        confirmColorScheme={deleteImpact?.will_ignore ? 'gray' : 'red'}
        isLoading={isDeleting}
      >
        <Box>
          <Text mb={3}>
            {deleteImpact?.will_ignore
              ? t('detailPanel.deleteDialog.ignoreMessage', { count: deleteImpact.transactions_preserved })
              : t('detailPanel.deleteDialog.message')}
          </Text>

          {/* Movement row */}
          {movement && (
            <Flex
              py={1}
              px={2}
              borderLeft="3px solid"
              borderLeftColor="redesign.accent.blue"
              bg="redesign.bg.card"
              align="center"
              mb={3}
            >
              <Box flex={1} minW={0}>
                <Text fontSize="xs" fontWeight="600" noOfLines={1}>
                  {movement.note || t('fallback.noNote')}
                </Text>
                <Text fontSize="2xs" color="redesign.text.muted" noOfLines={1}>
                  {movement.account?.name || t('fallback.unknown')}
                </Text>
              </Box>
              <Text
                fontSize="xs"
                fontWeight="600"
                ml={2}
                color={
                  movement.type === 'Income' ? 'redesign.accent.green' :
                  movement.type === 'Expenses' ? 'redesign.accent.red' :
                  'redesign.text'
                }
              >
                {formatMoney(movement.amount, movement.currency)}
              </Text>
            </Flex>
          )}

          {/* Source files that will be deleted */}
          {deleteImpact?.source_file_items?.length > 0 && (
            <Box mb={3}>
              <Text mb={1}>
                {deleteImpact.source_file_items.length === 1 ? t('detailPanel.deleteDialog.sourceFilesOne') : t('detailPanel.deleteDialog.sourceFilesOther')}
              </Text>
              <Wrap spacing={1}>
                {deleteImpact.source_file_items.map((sf) => (
                  <WrapItem key={sf.id}>
                    <Tooltip label={sf.filename} placement="top" hasArrow>
                      <Tag size="sm" maxW="180px" borderRadius="full" px={3} py={1}>
                        <Text isTruncated fontSize="sm">{sf.filename}</Text>
                      </Tag>
                    </Tooltip>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}

          {deleteImpact?.will_ignore ? (
            <>
              <Flex align="center" gap={2} bg="rgba(148, 163, 184, 0.14)" px={3} py={2} borderRadius="md">
                <Text fontSize="sm" color="redesign.text.muted" fontWeight="500">
                  {t('detailPanel.deleteDialog.ignoreReassurance', { count: deleteImpact.transactions_preserved })}
                </Text>
              </Flex>
              <IgnoreReasonField value={ignoreReason} onChange={setIgnoreReason} mt={3} />
            </>
          ) : (
            <Flex align="center" gap={2} bg="rgba(239, 68, 68, 0.10)" px={3} py={2} borderRadius="md">
              <WarningIcon color="redesign.accent.red" boxSize={4} flexShrink={0} />
              <Text fontSize="sm" color="redesign.accent.red" fontWeight="500">
                {t('detailPanel.deleteDialog.warning')}
              </Text>
            </Flex>
          )}
        </Box>
      </ConfirmationDialog>

      <ConfirmationDialog
        isOpen={showMissingFieldsDialog}
        onClose={() => setShowMissingFieldsDialog(false)}
        onConfirm={() => {
          setShowMissingFieldsDialog(false);
          isCreateMode ? doCreate() : doUpdate();
        }}
        message={getMissingFieldsMessage(editedMovement) || ''}
        showWarningIcon
        isLoading={updating}
      />
    </Box>
  );
}
